/**
 * PATH: packages/bills/src/components/billReadModel.ts
 * FILE_ID: BILLS-PKG-CMP-READMODEL-001
 * PURPOSE: Pure transform: BillDetail → a structured "read" model
 *          (header rows, totals, tax summary, line grid) for the
 *          clay/excel StructuredBillRead view.
 *
 * WHY: The operator asked to "see the read sections in an excel-like box"
 *      in the standard bill format. Keeping the shaping + formatting as a
 *      pure I→O function makes the render component dumb and lets us pin
 *      the money/date formatting + key mapping in cheap unit tests
 *      (p1b/p1c). No React, no side effects.
 */

import type { BillDetail, BillLineItem } from '../types';
import { lineFieldKey, type BillFieldKey, type BillLineColumn } from './billFieldKeys';

export interface ReadCell {
  fieldKey: BillFieldKey;
  label: string;
  value: string;
  /** Right-align + tabular for money/number cells. */
  numeric?: boolean;
}

export interface ReadSection {
  id: string;
  title: string;
  cells: ReadCell[];
}

export interface ReadLineCell {
  fieldKey: BillFieldKey;
  column: BillLineColumn;
  value: string;
  numeric?: boolean;
}

export interface ReadLineRow {
  idx: number;
  cells: ReadLineCell[];
}

/** Format a DRF money string/number into a grouped 2dp display. */
export function formatMoney(
  value: string | number | null | undefined,
  currency?: string,
): string {
  if (value == null || value === '') return '—';
  const n = typeof value === 'number' ? value : parseFloat(String(value));
  if (!Number.isFinite(n)) return String(value);
  const body = n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${currency} ${body}` : body;
}

/** Format an ISO date (yyyy-mm-dd or full) into a short readable date. */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function text(value: string | null | undefined): string {
  const v = (value ?? '').toString().trim();
  return v === '' ? '—' : v;
}

/** Vendor identity block. */
export function buildVendorSection(bill: BillDetail): ReadSection {
  return {
    id: 'vendor',
    title: 'Supplier',
    cells: [
      { fieldKey: 'vendor_name', label: 'Name', value: text(bill.vendor_name) },
      { fieldKey: 'vendor_abn', label: 'ABN', value: text(bill.vendor_abn) },
      { fieldKey: 'vendor_email', label: 'Email', value: text(bill.vendor_email) },
    ],
  };
}

/** Bill meta block (numbers + dates). */
export function buildBillMetaSection(bill: BillDetail): ReadSection {
  return {
    id: 'meta',
    title: 'Bill details',
    cells: [
      { fieldKey: 'invoice_number', label: 'Bill #', value: text(bill.invoice_number) },
      { fieldKey: 'reference', label: 'Reference', value: text(bill.reference) },
      { fieldKey: 'invoice_date', label: 'Bill date', value: formatDate(bill.invoice_date) },
      { fieldKey: 'due_date', label: 'Due date', value: formatDate(bill.due_date) },
    ],
  };
}

/** Totals block (subtotal / GST / total + currency). */
export function buildTotalsSection(bill: BillDetail): ReadSection {
  const ccy = bill.currency || undefined;
  return {
    id: 'totals',
    title: 'Totals',
    cells: [
      { fieldKey: 'subtotal', label: 'Subtotal', value: formatMoney(bill.subtotal, ccy), numeric: true },
      { fieldKey: 'gst', label: 'GST', value: formatMoney(bill.gst, ccy), numeric: true },
      { fieldKey: 'total', label: 'Total', value: formatMoney(bill.total, ccy), numeric: true },
      { fieldKey: 'currency', label: 'Currency', value: text(bill.currency) },
    ],
  };
}

function lineValue(item: BillLineItem, column: BillLineColumn, currency?: string): string {
  switch (column) {
    case 'description':
      return text(item.description);
    case 'quantity':
      return item.quantity == null ? '—' : String(item.quantity);
    case 'unit_amount':
      return formatMoney(item.unit_amount as number | null, currency);
    case 'line_total':
      return formatMoney(item.line_total as number | null, currency);
  }
}

/** Line-item grid rows in canonical column order. */
export function buildLineRows(bill: BillDetail): ReadLineRow[] {
  const ccy = bill.currency || undefined;
  const columns: BillLineColumn[] = ['description', 'quantity', 'unit_amount', 'line_total'];
  return (bill.line_items || []).map((item, idx) => ({
    idx,
    cells: columns.map((column) => ({
      fieldKey: lineFieldKey(idx, column),
      column,
      value: lineValue(item, column, ccy),
      numeric: column !== 'description',
    })),
  }));
}

/** The full read model in display order. */
export function buildBillReadModel(bill: BillDetail): {
  sections: ReadSection[];
  lines: ReadLineRow[];
} {
  return {
    sections: [
      buildVendorSection(bill),
      buildBillMetaSection(bill),
      buildTotalsSection(bill),
    ],
    lines: buildLineRows(bill),
  };
}
